import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, getApiErrorMessage } from '../lib/api'
import { formatMenuInstant } from '../lib/date'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { RoleLabels } from '../auth/rbac'
import { signupRequestStatusLabel } from '../lib/signupRequestStatus'

export default function SignupRequestDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const decisionStatus = String(item?.status || '').toUpperCase()
  const isPending = decisionStatus === 'PENDING'
  const processedMessage = item && !isPending
    ? ((item.decidedByName ? item.decidedByName + ' 관리자' : '다른 관리자')
      + '가 이미 이 가입 신청을 ' + (decisionStatus === 'REJECTED' ? '반려' : '승인') + ' 처리했습니다.')
    : ''

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get(`/signups/${id}`)
      const payload = res?.data?.data ?? res?.data
      setItem(payload)
    } catch (e) {
      setError('가입 신청 상세를 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const handleDecision = async (action) => {
    if (!isPending) {
      setError(processedMessage)
      return
    }
    const ok = window.confirm(action === 'approve' ? '해당 가입 신청을 승인하시겠습니까?' : '해당 가입 신청을 거절하시겠습니까?')
    if (!ok) return
    setLoading(true)
    setError(null)
    try {
      await api.post(`/signups/${id}/${action}`, {})
      navigate('/signups')
    } catch (e) {
      setError(getApiErrorMessage(e, '처리 중 오류가 발생했습니다.'))
      await load()
    } finally {
      setLoading(false)
    }
  }

  if (loading && !item) {
    return (
      <div className="py-8 flex justify-center">
        <Loading label="불러오는 중..." />
      </div>
    )
  }

  if (error && !item) {
    return <InlineError message={error} />
  }

  if (!item) {
    return null
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">가입 신청 상세 #{item.id}</h1>
        <div className="space-x-2">
          <button
            type="button"
            disabled={!isPending || loading}
            onClick={() => handleDecision('approve')}
            className="px-3 py-1 rounded bg-emerald-600 text-white text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            승인
          </button>
          <button
            type="button"
            disabled={!isPending || loading}
            onClick={() => handleDecision('reject')}
            className="px-3 py-1 rounded bg-red-600 text-white text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            거절
          </button>
        </div>
      </div>

      {!isPending && processedMessage && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {processedMessage}
        </div>
      )}
      {error && <InlineError message={error} />}

      <div className="border rounded-md p-4 space-y-2 text-sm">
        <div><span className="font-semibold">이메일:</span> {item.email}</div>
        <div><span className="font-semibold">이름:</span> {item.name}</div>
        <div><span className="font-semibold">휴대폰:</span> {item.phoneNumber || '-'}</div>
        <div><span className="font-semibold">조직:</span> {item.orgName ?? '-'}</div>
        {item.personalJoin && (
          <>
            <div><span className="font-semibold">개인 가입:</span> 예</div>
            {item.affiliationText && <div><span className="font-semibold">현재 소속(입력):</span> {item.affiliationText}</div>}
            <div><span className="font-semibold">소속명 조직 생성·할당 동의:</span> {item.affiliationOrgCreateConsent ? '예' : '아니오'}</div>
            <p className="text-xs text-slate-500">
              개인 가입 시 입력·생성된 표기명은 설계의뢰 알림·진료분석 리포트 등에 그대로 쓰이므로, 승인 전 표기가 적절한지 확인해 주세요.
            </p>
          </>
        )}
        <div><span className="font-semibold">신청 역할:</span> {RoleLabels[item.requestedRole] || item.requestedRole}</div>
        <div><span className="font-semibold">상태:</span> {signupRequestStatusLabel(item.status)}</div>
        <div><span className="font-semibold">신청일:</span> {formatMenuInstant(item, 'createdAt')}</div>
        <div><span className="font-semibold">처리일:</span> {formatMenuInstant(item, 'decidedAt')}</div>
        <div><span className="font-semibold">처리자:</span> {item.decidedByName || '-'}</div>
        <div><span className="font-semibold">사유:</span> {item.decisionReason || '-'}</div>
      </div>
    </div>
  )
}


